import { message } from 'antd';
import { calcNewSortOrder } from 'common/Utility';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';

/**
 * Single hook that manages all DataTag-related state and logic
 * Extracted from DataTagList component to improve maintainability
 * 
 * @param {Array} dataTagList - Array of data tags from props
 * @param {Function} onTagReorder - Callback for tag reordering
 * @returns {Object} All state and handlers needed by DataTagList component
 */
const useDataTagManager = (dataTagList, onTagReorder) => {
  const [tags, setTags] = useState(dataTagList);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    businessArea: '',
    updatedBy: '',
    lastUpdated: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascend' });
  const [pinnedTags, setPinnedTags] = useState([]);
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const processedTags = useMemo(() => {
    let result = [...tags];

    if (searchTerm) {
      const normalizedSearchTerm = searchTerm.toLowerCase();
      result = result.filter(tag => {
        const searchableString = [
          tag.tagName?.toLowerCase(),
          tag.tagDescription?.toLowerCase(),
          tag.businessAreaName?.toLowerCase(),
          tag.createdByName?.toLowerCase(),
          tag.updatedByName?.toLowerCase(),
          tag.createdByEmail?.toLowerCase(),
          tag.updatedByEmail?.toLowerCase(),
          tag.createdDate && moment(tag.createdDate).format('YYYY-MM-DD').toLowerCase(),
          tag.updatedDate && moment(tag.updatedDate).format('YYYY-MM-DD').toLowerCase(),
        ]
          .filter(Boolean)
          .join(' ');

        return searchableString.includes(normalizedSearchTerm);
      });
    }
    if (filters.businessArea) {
      result = result.filter(tag => tag.businessAreaName === filters.businessArea);
    }
    if (filters.updatedBy) {
      result = result.filter(tag => tag.updatedByEmail === filters.updatedBy);
    }

    if (filters.lastUpdated) {
      result = result.filter(tag => {
        const tagDate = moment(tag.updatedDate);
        const now = moment();
        switch (filters.lastUpdated) {
          case 'Today':
            return tagDate.isSame(now, 'day');
          case 'This-Week':
            return tagDate.isSame(now, 'week');
          case 'This-Month':
            return tagDate.isSame(now, 'month');
          default:
            return true;
        }
      });
    }

    if (showPinnedOnly) {
      result = result.filter(tag => pinnedTags.includes(tag.tagId));
    }
    if (sortConfig.key) {
      result = result.slice().sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const aLower = aValue.toLowerCase();
          const bLower = bValue.toLowerCase();
          if (aLower < bLower) {
            return sortConfig.direction === 'ascend' ? -1 : 1;
          }
          if (aLower > bLower) {
            return sortConfig.direction === 'ascend' ? 1 : -1;
          }
          return 0;
        }
        if (aValue < bValue) {
          return sortConfig.direction === 'ascend' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascend' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [tags, searchTerm, filters, sortConfig, pinnedTags, showPinnedOnly]);

  const businessAreas = useMemo(() => 
    Array.from(new Set(tags.map(tag => tag.businessAreaName))), 
    [tags]
  );

  const updatedByUsers = useMemo(() => 
    Array.from(new Set(tags.map(tag => tag.updatedByEmail))), 
    [tags]
  );

  const getBusinessAreaColors = useMemo(() => {
    const colorSchemes = [
      { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500' },
      { bg: 'bg-indigo-200', text: 'text-indigo-800', border: 'border-indigo-500' },
      { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-500' },
      { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-500' },
      { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-500' },
      { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-500' },
      { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-500' },
    ];
    const businessAreaColors = new Map();
    
    return (businessArea) => {
      if (businessAreaColors.has(businessArea)) {
        return businessAreaColors.get(businessArea);
      }
      const colorScheme = colorSchemes[businessAreaColors.size % colorSchemes.length];
      businessAreaColors.set(businessArea, colorScheme);
      return colorScheme;
    };
  }, []);

  useEffect(() => {
    setTags(dataTagList);
  }, [dataTagList]);

  useEffect(() => {
    const storedPinnedTags = JSON.parse(localStorage.getItem('pinnedTags') || '[]');
    setPinnedTags(storedPinnedTags);
  }, []);

  // Initialize business area colors for all tags
  useEffect(() => {
    businessAreas.forEach(area => getBusinessAreaColors(area));
  }, [businessAreas, getBusinessAreaColors]);

  
  const handlePinTag = (tagId) => {
    const updatedPinnedTags = pinnedTags.includes(tagId)
      ? pinnedTags.filter(id => id !== tagId)
      : [...pinnedTags, tagId];
    setPinnedTags(updatedPinnedTags);
    localStorage.setItem('pinnedTags', JSON.stringify(updatedPinnedTags));
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      message.error('Invalid drop location');
      return;
    }
    
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    
    if (sourceIndex === destinationIndex) {
      message.info('No changes made to the order');
      return;
    }
    
    const items = Array.from(tags);
    const newSortOrder = calcNewSortOrder(items, sourceIndex, destinationIndex);
    const [reorderedItem] = items.splice(sourceIndex, 1);
    const oldSortOrder = reorderedItem.sortOrder;
    reorderedItem.sortOrder = newSortOrder;
    items.splice(destinationIndex, 0, reorderedItem);
    setTags(items);
    
    const datatag = {
      tagId: reorderedItem.tagId,
      oldSortOrder,
      sortOrder: newSortOrder,
    };
    
    onTagReorder(datatag);
    message.loading('Reordering tags...');
  };

  
  const clearSorting = () => {
    setSortConfig({ key: null, direction: 'ascend' });
  };

  const isFilterActive = () => Object.values(filters).some(Boolean) || showPinnedOnly;

  const isSortActive = () => sortConfig.key !== null;
  return {
    // Data state
    tags,
    filteredTags: processedTags,
    businessAreas,
    updatedByUsers,
    
    // Search & Filter state
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    sortConfig,
    setSortConfig,
    
    // Pinning state
    pinnedTags,
    showPinnedOnly,
    setShowPinnedOnly,
    handlePinTag,
    
    // Selection state
    selectedRowKeys,
    setSelectedRowKeys,
    
    // Handlers
    onDragEnd,
    clearSorting,
    
    // Utility functions
    getBusinessAreaColors,
    isFilterActive,
    isSortActive,
  };
};

export default useDataTagManager;
